/**
 * React Query hooks for fetching location tags
 *
 * @see GitLab #999 — Support for adding location tags
 */

import { useQuery } from '@tanstack/react-query';

import { useNetworkStatus } from './useOfflineRequestsQuery';
import { api } from '../../services/api';
import {
  getOfflineLocationTagList,
  mirrorLocationTagsToOfflineCache,
} from '../../services/locationTagOfflineSync';
import {
  isOfflineClientRequestId,
  isPersistedServerRequestId,
} from '../../utils/locationTagRequestId';

import type {
  LocationTag,
  LocationTagsResponse,
} from '../../types/locationTag';

export const locationTagsKeys = {
  all: ['locationTags'] as const,
  lists: () => [...locationTagsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...locationTagsKeys.lists(), requestId] as const,
};

export const useLocationTags = (requestId: string, enabled = true) => {
  const isOnline = useNetworkStatus();
  const canFetch =
    isPersistedServerRequestId(requestId) ||
    isOfflineClientRequestId(requestId);

  return useQuery({
    queryKey: locationTagsKeys.list(requestId),
    networkMode: 'always',
    queryFn: async (): Promise<LocationTag[]> => {
      if (!isOnline || isOfflineClientRequestId(requestId)) {
        return getOfflineLocationTagList(requestId);
      }

      const response = await api.get<LocationTagsResponse>(
        `/requests/${requestId}/location-tags`,
      );
      await mirrorLocationTagsToOfflineCache(requestId, response.locationTags);
      return response.locationTags;
    },
    enabled: canFetch && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch location tags.',
    },
  });
};
